import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setProgress } from "../../redux/slice/system";

import { ModuleBlockTitle, ModuleExpandIcon } from "../diagram/components";
import { formatTbNumberColumns, calculateTbSum } from "../../service/table2";

import { classNames } from "primereact/utils";
import { Paginator } from "primereact/paginator";

const useRawTable = ({
  setting = {},
  nullTransfer = "-",
  subTitleBgColor = null,
  rows = 10,
} = {}) => {
  const dispatch = useDispatch();
  const [tbData, setTbData] = useState({ raw: null, format: null });
  const [tbFirstIndex, setTbFirstIndex] = useState(0);
  const [tbHead, setTbHead] = useState({ columns: null });
  const [tbBody, setTbBody] = useState({ columns: null });
  const [tbFoot, setTbFoot] = useState({ data: null, columns: null });
  const [tbExpandColumns, setTbExpandColumns] = useState({});

  // 產生 Foot 加總資料&欄位、側邊欄 加總資料
  useEffect(() => {
    if (!tbHead.columns || !tbBody.columns || !tbData.raw) return;

    let { formatData, footData } = calculateTbSum({
        isPercent: setting.isPercent,
        data: tbData.raw,
        columns: tbBody.columns,
        nullTransfer,
      }),
      footColumns = [],
      groupKeys = (setting.groupColumns || []).map((column) => column.key);

    footColumns = [
      [
        ...(setting.isGroup && setting.groupColumns.length > 0
          ? [
              {
                key: "total_col",
                field: "total_col_format",
                footer: "總數",
                colSpan: tbHead.columns[0].reduce(
                  (pre, cur) =>
                    (pre += groupKeys.includes(cur.key) ? cur.colSpan : 0),
                  0
                ),
                rowSpan: 1,
              },
            ]
          : []),
        ...tbBody.columns
          .filter((column) => !groupKeys.includes(column.key))
          .map((column) => ({
            ...column,
            footer: footData[column.key],
            rowSpan: 1,
          })),
      ],
    ];

    setTbData((state) => ({
      ...state,
      format: formatData,
    }));
    setTbFoot({
      data: footData,
      columns: footColumns,
    });
  }, [tbHead, tbBody, tbData.raw]);

  // 處理 Head、Body 欄位
  useEffect(() => {
    let groupKeys = (setting.groupColumns || []).map((column) => column.key);

    // 取得當前樹狀結構最深層數
    const getMaxLayer = ({ childs, layer }) => {
      let maxLayer = 0;
      childs.forEach((item) => {
        let tempLayer = 0;

        if (
          item.child &&
          (!item.isControl ||
            (item.isControl && tbExpandColumns[item.key]) ||
            setting.isPercent)
        ) {
          tempLayer = getMaxLayer({ childs: item.child, layer: layer + 1 });
        } else {
          tempLayer = layer;
        }

        if (tempLayer > maxLayer) maxLayer = tempLayer;
      });

      return maxLayer;
    };

    // 檢查是否需要擴充欄位 (ex: 數量、百分比、小計)
    const checkColumns = ({ childs }) => {
      childs.forEach((item) => {
        if (
          item.child &&
          (!item.isControl || (item.isControl && tbExpandColumns[item.key]))
        ) {
          if (item.isSum)
            item.child.push({
              key: item.key,
              field: item.key,
              isSkip: true, //加總時跳過 小計項目
              header: "小計",
            });
          checkColumns({ childs: item.child });
        } else if (
          setting.isPercent &&
          ((!item.child && !item.isControl) ||
            (item.child && item.isControl && !tbExpandColumns[item.key])) &&
          !groupKeys.includes(item.key)
        ) {
          item.child = [
            {
              key: item.key,
              field: `${item.key}_format`,
              isSkip: !!item.isSkip,
              header: "數量",
            },
            {
              key: `${item.key}_percent`,
              field: `${item.key}_percent`,
              header: "百分比",
            },
          ];
        }
      });
    };

    // 加入 欄位層級(layer)/合併大小(colSpan、rowSpan)
    const addSettingKeys = ({ childs }) => {
      let childLength = 0,
        maxSiblingLayer = -1;
      childs.forEach((item) => {
        if (
          item.child &&
          (!item.isControl ||
            (item.isControl && tbExpandColumns[item.key]) ||
            setting.isPercent)
        ) {
          let tempChildLength = addSettingKeys({ childs: item.child });
          item.colSpan = tempChildLength;
          childLength += tempChildLength - 1; // 扣除父子層重複

          item.layer = getMaxLayer({ childs: item.child, layer: 1 }) + 1;
        } else {
          item.colSpan = 1;

          item.layer = 1;
        }

        if (item.layer > maxSiblingLayer) maxSiblingLayer = item.layer;
      });

      childs.forEach((item) => {
        item.rowSpan = maxSiblingLayer - item.layer + 1;
      });

      return childs.length + childLength;
    };

    // 整理 header & body 呈現欄位
    const createColumns = ({ headColumns, bodyColumns, childs, layer }) => {
      childs.forEach((item) => {
        if (!headColumns[layer]) headColumns[layer] = [];
        if (item.key === "" && item.header === "") item.header = "無";
        headColumns[layer].push(item);

        if (item.layer === 1) bodyColumns.push(item);

        if (
          item.child &&
          (!item.isControl ||
            (item.isControl && tbExpandColumns[item.key]) ||
            setting.isPercent)
        ) {
          createColumns({
            headColumns,
            bodyColumns,
            childs: item.child,
            layer: layer + item.rowSpan,
          });
        }
      });

      return { headColumns, bodyColumns };
    };

    let allColumns = [];
    if (setting.isGroup) allColumns = [...allColumns, ...setting.groupColumns];
    allColumns = [...allColumns, ...(setting.columns || [])];
    if (setting.isRowSum)
      allColumns = [
        ...allColumns,
        {
          key: "total_row",
          field: "total_row_format",
          header: "總數",
        },
      ];
    allColumns = JSON.parse(JSON.stringify(allColumns));

    checkColumns({ childs: allColumns });
    addSettingKeys({ childs: allColumns });
    let { headColumns, bodyColumns } = createColumns({
      headColumns: [],
      bodyColumns: [],
      childs: allColumns,
      layer: 0,
    });

    setTbHead({ columns: headColumns });
    setTbBody({ columns: bodyColumns });
  }, [setting, tbExpandColumns]);

  // 欄位展開
  const handleTbExpand = ({ key }) => {
    setTbExpandColumns((state) => ({
      ...state,
      [key]: !state[key],
    }));
  };

  const getTbBodyData = ({ isLazy, isPaginator }) => {
    let data = tbData.format || [];
    if (isLazy) data = data.slice(tbFirstIndex, tbFirstIndex + rows);
    // if (isPaginator) data = data.slice(tbFirstIndex, tbFirstIndex + rows);
    return data;
  };

  const renderTable = useCallback(
    ({
      moduleTitle = "",
      isExport = true,
      isFilter = true,
      isLazy = false,
      isPaginator = false,
      callMainAPI,
    }) => {
      return (
        <>
          <ModuleBlockTitle
            type="table"
            moduleTitle={moduleTitle}
            isExport={isExport}
            isFilter={isFilter}
            options={{
              setting,
              tbData,
              tbHead,
              tbBody,
              tbFoot,
              tbExpandColumns,
              nullTransfer,
              callMainAPI,
              progressAction: (flag) => dispatch(setProgress({ show: flag })),
            }}
          />

          <div className="max-h-30rem overflow-auto">
            <table className="w-full bg-white custom-tb">
              <thead>
                {(tbHead.columns || []).map((columns, csIdx) => (
                  <tr key={`thead_${csIdx}`}>
                    {columns.map((column, cIdx) => (
                      <th
                        key={`thead_${csIdx}_${cIdx}`}
                        colSpan={column.colSpan}
                        rowSpan={column.rowSpan}
                        className={classNames("white-space-prewrap", {
                          hidden: column.isHide,
                          "bg-tb-subtitle": !subTitleBgColor,
                          "bg-tb-elderlytitle1":
                            subTitleBgColor === "elderlytitle1",
                          "bg-tb-elderlytitle2":
                            subTitleBgColor === "elderlytitle2",
                        })}
                      >
                        <div className="flex align-items-center justify-content-center">
                          <span>{column.header}</span>
                          {column.isControl && (
                            <ModuleExpandIcon
                              handleExpand={() =>
                                handleTbExpand({ key: column.key })
                              }
                            />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="text-center">
                {getTbBodyData({ isLazy, isPaginator }).map((item, index) => (
                  <tr
                    key={`tbody_${index}`}
                    className={classNames(item.className ?? "")}
                  >
                    {(tbBody.columns || []).map((column, cIdx) => (
                      <React.Fragment key={`tbody_${index}_${cIdx}`}>
                        {(setting.groupColumns || []).some(
                          (group) => group.key === column.key
                        ) ? (
                          <>
                            {item[`rowSpan_${column.key}`] !== null && (
                              <td
                                rowSpan={item[`rowSpan_${column.key}`] ?? 1}
                                className={classNames({
                                  "bg-tb-thirdtitle": !item.isFoot,
                                  "bg-tb-fifthtitle font-bold": !!item.isFoot,
                                  hidden: column.isHide,
                                })}
                              >
                                {item[column.field] !== "null"
                                  ? item[column.field]
                                  : nullTransfer}
                                {column.isGroupControl &&
                                  item[column.field] && (
                                    <ModuleExpandIcon
                                      handleExpand={() =>
                                        handleTbExpand({ key: column.key })
                                      }
                                    />
                                  )}
                              </td>
                            )}
                          </>
                        ) : (
                          <td
                            className={classNames({
                              "bg-tb-fifthtitle font-bold":
                                ["total_row", "total_row_percent"].includes(
                                  column.key
                                ) || !!item.isFoot,
                              hidden: column.isHide,
                            })}
                          >
                            {item[column.field] ?? nullTransfer}
                            {column.key.includes("_percent") ? "%" : ""}
                          </td>
                        )}
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
              {setting.isColSum && (
                <tfoot>
                  {(tbFoot.columns || []).map((columns, csIdx) => (
                    <tr key={`tfoot_${csIdx}`} className="bg-tb-fifthtitle">
                      {columns.map((column, cIdx) => (
                        <th
                          key={`tfoot_${csIdx}_${cIdx}`}
                          colSpan={column.colSpan ?? 1}
                          className={classNames({
                            hidden: column.isHide,
                          })}
                        >
                          {!!column.isFootSumHide ? (
                            <></>
                          ) : (
                            <>
                              {column.key === "total_col"
                                ? column.footer
                                : tbFoot.data[`${column.field}`]}
                              {column.key.includes("_percent") ? "%" : ""}
                            </>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </tfoot>
              )}
            </table>

            {isPaginator && tbData.format && (
              <Paginator
                totalRecords={tbData.totalRecord}
                first={tbFirstIndex}
                rows={rows}
                onPageChange={(e) => {
                  setTbFirstIndex(e.first);
                  if (callMainAPI) callMainAPI({ page: e.page + 1 });
                }}
                template={{
                  layout:
                    "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
                  CurrentPageReport: (options) => {
                    return (
                      <span
                        className="text-center ml-2"
                        style={{
                          color: "var(--text-color)",
                          userSelect: "none",
                        }}
                      >
                        {Math.ceil(options.first / rows)}
                        <span className="mx-1">/</span>
                        {Math.ceil(options.totalRecords / rows)}
                      </span>
                    );
                  },
                }}
              ></Paginator>
            )}
          </div>
        </>
      );
    },
    [tbHead, tbBody, tbFoot, tbData, tbFirstIndex]
  );

  return { setTbData, setTbFirstIndex, renderTable };
};

export default useRawTable;
